import { FC, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ICoupon } from '../../../models/coupons.model';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';

import { useSnackbar } from 'notistack';
import { DatePicker, Descriptions, Select } from 'antd';


import Loader from '../../Loader';
import Button from '../../Button';
import SelectCampusModal from '../../modals/SelectCampusModal2';
import { errorVariant, infoVariant, warningVariant } from '../../../lib/barVariant';
import { doGetCouponCampuses } from '../../../store/couponCampus.slice';
import useAuth from '../../../hooks/useAuth';
import MultiCampusModal from '../../statistics/regular-test/modals/MultiCampusModal';
import SelectCampusMultiChoice from '../../SelectCampusMultiChoice';
const { Option } = Select;

interface IProps {
  coupon: ICoupon|null;
  onSubmit: Function;
}

const BusinessCouponEditor: FC<IProps> = ({ coupon, onSubmit }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // PA0001 = 결제대상
  const PA0001 = commonCodes.find(item => item.commonCode === 'PA0001')?.detailCode;

  const { isLoading } = useSelector((state: RootState) => state.couponCampus);
  const history = useHistory();
  const location = useLocation();

  const [campusCodes, setCampusCodes] = useState<string[]>([]);

  const [name, setName] = useState('');
  const [amount, setAmount] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [validDays, setValidDays] = useState<any>(null);
  const [target, setTarget] = useState<any>(null);

  const [multiCampusVisible, setMultiCampusVisible] = useState(false);

  const nameInput = useRef<any>();
  const amountInput = useRef<any>();
  const validDaysInput = useRef<any>();

  useEffect(() => {
    if (coupon) {
      setName(coupon.couponName);
      setAmount(coupon.amount);
      setStartDate(coupon.createSrtDate);
      setEndDate(coupon.createEndDate);
      setValidDays(coupon.validDays);
      setTarget(coupon.paymentTargetCode);
      getCouponCampus(coupon.couponCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [coupon]);

  const getCouponCampus = async (couponCode: number) => {
    const res = await dispatch(doGetCouponCampuses(couponCode));

    switch (res?.status) {
      case 200:
        const data = res?.data?.data;
        setCampusCodes(data.map((item: any) => item.campusCode));
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }
  
  const onSave = async () => {
    if (!name) {
      nameInput.current.focus();
      return enqueueSnackbar(t('business.coupon.message1'), infoVariant);
    }
    if (!amount) {
      amountInput.current.focus();
      return enqueueSnackbar(t('business.coupon.message2'), infoVariant);
    }
    if (!startDate) {
      return enqueueSnackbar(t('business.coupon.message3'), infoVariant);
    }
    if (!validDays) {
      validDaysInput.current.focus();
      return enqueueSnackbar(t('business.coupon.message4'), infoVariant);
    }
    if (!target) {
      return enqueueSnackbar(t('business.coupon.message5'), infoVariant);
    }

    const req: any = {
      couponName: name,
      couponType: 'OT',
      allCampusYn: campusCodes.length ? 'N' : 'Y',
      amount,
      createSrtDate: startDate,
      createEndDate: endDate || '30001231',
      validDays,
      paymentTargetCode: target
    };

    if (campusCodes.length) {
      req.modifyCampusCode = 'Y';
      req.campusCode = campusCodes;
    } else {
      req.modifyCampusCode = 'N';
    }

    onSubmit(req);
  }

  return (
    <div className='comm-table__area rowType'>
      {isLoading ? <Loader /> : null}
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label={t('business.coupon.column7')} span={2}>
          <div className="flex">
            <div className="comm-btn__area">
              <SelectCampusMultiChoice
                placeholder={t('statistics.eLearning.title2')}
                onChange={setCampusCodes}
                value={campusCodes}
              />
            </div>
            <div className="v-line ml-2">
              {
                campusCodes?.length
                  ? `(${campusCodes.length}${t('board.notice.column9')})`
                  : `(${t('common.text.all')})`
              }
            </div>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('business.coupon.placeholder1')} span={2}>
          <input
            type="text"
            className="form-control input-text"
            value={name}
            onChange={e => setName(e.target.value)}
            ref={nameInput}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('business.coupon.column8')} span={2}>
          <div className="w-210p flex">
            <input
              type="number"
              className="form-control input-text"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              ref={amountInput}
            />
            <span className="v-line ml-1">{t('business.coupon.column9')}</span>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('business.coupon.column2')}>
          <div className="date-picker__area">
            <DatePicker
              value={startDate ? moment(startDate) : undefined}
              onChange={e => {
                if (e === null) setStartDate('');
                else setStartDate(moment(e).format('YYYYMMDD'))
              }}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('business.coupon.column3')}>
          <div className="date-picker__area">
            <DatePicker
              value={endDate ? moment(endDate) : undefined}
              onChange={e => {
                if (e === null) setEndDate('');
                else setEndDate(moment(e).format('YYYYMMDD'))
              }}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('business.coupon.column10')} span={2}>
          <div className="w-210p flex">
            <input
              type="number"
              className="form-control input-text"
              value={validDays}
              onChange={e => setValidDays(e.target.value)}
              ref={validDaysInput}
            />
            <span className="v-line ml-1">{t('business.coupon.column12')}</span>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('business.coupon.column11')} span={2}>
          <div className="w-210p">
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={target}
              onChange={value => setTarget(value)}
            >
              {
                PA0001?.map((item: any) => {
                  return (
                    <Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Option>
                  )
                })
              }
            </Select>
          </div>          
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSave}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/business/coupon${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>

      {multiCampusVisible &&
        <MultiCampusModal      
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }
    </div>
  )
}

export default BusinessCouponEditor;
